/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { RuleMatchType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionnaireQuestionResponseProps {
  id: string;
  questionnaireResponseId: string;
  questionnaireSectionQuestionId: string;
  matchType: RuleMatchType;
  responseValue: string | null;
  explanation: string | null;
  reviewRequired: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionnaireQuestionResponseRecord extends Record<QuestionnaireQuestionResponseProps>(
  {
    id: null as any,
    questionnaireResponseId: null as any,
    questionnaireSectionQuestionId: null as any,
    matchType: null as any,
    responseValue: null as any,
    explanation: null as any,
    reviewRequired: null as any,
    companyId: null as any,
    dateLastUpdated: null as any,
    dateCreated: null as any,
    dateDeleted: null as any,
    lastUpdatedById: null as any,
  },
) {
  public static fromJS(json: {
    id: string;
    questionnaireResponseId: string;
    questionnaireSectionQuestionId: string;
    matchType: RuleMatchType;
    responseValue: string | null;
    explanation: string | null;
    reviewRequired: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionnaireQuestionResponseRecord {
    if (json == null) {
      throw new Error('QuestionnaireQuestionResponseRecord.fromJS: parameter cannot be null');
    }
    return new QuestionnaireQuestionResponseRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireQuestionResponseJson.id',
      ),
      questionnaireResponseId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'questionnaireResponseId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireQuestionResponseJson.questionnaireResponseId',
      ),
      questionnaireSectionQuestionId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(
            j,
            'questionnaireSectionQuestionId',
            '00000000-0000-0000-0000-000000000000',
          ),
        'QuestionnaireQuestionResponseJson.questionnaireSectionQuestionId',
      ),
      matchType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'matchType', RuleMatchType.Unspecified),
        'QuestionnaireQuestionResponseJson.matchType',
      ),
      responseValue: json.responseValue,
      explanation: json.explanation,
      reviewRequired: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'reviewRequired', false),
        'QuestionnaireQuestionResponseJson.reviewRequired',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireQuestionResponseJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    questionnaireResponseId: string;
    questionnaireSectionQuestionId: string;
    matchType: RuleMatchType;
    responseValue: string | null;
    explanation: string | null;
    reviewRequired: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionnaireQuestionResponseRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionnaireQuestionResponseRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionnaireQuestionResponseRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireQuestionResponseJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireResponseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'questionnaireResponseId',
                '00000000-0000-0000-0000-000000000000',
              ),
            'QuestionnaireQuestionResponseJson.questionnaireResponseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireSectionQuestionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'questionnaireSectionQuestionId',
                '00000000-0000-0000-0000-000000000000',
              ),
            'QuestionnaireQuestionResponseJson.questionnaireSectionQuestionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      matchType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'matchType', RuleMatchType.Unspecified),
            'QuestionnaireQuestionResponseJson.matchType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      responseValue: tryOrDefault(
        () => json.responseValue,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      explanation: tryOrDefault(
        () => json.explanation,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reviewRequired: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'reviewRequired', false),
            'QuestionnaireQuestionResponseJson.reviewRequired',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireQuestionResponseJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionnaireQuestionResponseProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionnaireQuestionResponseRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionnaireQuestionResponseViewModelBase
  implements QuestionnaireQuestionResponseProps
{
  protected readonly _props: QuestionnaireQuestionResponseProps;
  private _json?: QuestionnaireQuestionResponseProps;

  get id(): string {
    return this._props.id;
  }
  get questionnaireResponseId(): string {
    return this._props.questionnaireResponseId;
  }
  get questionnaireSectionQuestionId(): string {
    return this._props.questionnaireSectionQuestionId;
  }
  get matchType(): RuleMatchType {
    return this._props.matchType;
  }
  get responseValue(): string | null {
    return this._props.responseValue;
  }
  get explanation(): string | null {
    return this._props.explanation;
  }
  get reviewRequired(): boolean {
    return this._props.reviewRequired;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isDropoffLocation() {
    return this._props.matchType === RuleMatchType.DropoffLocation;
  }
  public get isPickupLocation() {
    return this._props.matchType === RuleMatchType.PickupLocation;
  }
  public get isUnspecified() {
    return this._props.matchType === RuleMatchType.Unspecified;
  }

  public isAnyMatchType(...ruleMatchType: RuleMatchType[]) {
    return ruleMatchType.includes(this._props.matchType);
  }

  constructor(props: QuestionnaireQuestionResponseProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        questionnaireResponseId: this._props.questionnaireResponseId,
        questionnaireSectionQuestionId: this._props.questionnaireSectionQuestionId,
        matchType: this._props.matchType,
        responseValue: this._props.responseValue,
        explanation: this._props.explanation,
        reviewRequired: this._props.reviewRequired,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionnaireQuestionResponseEntities = (state: AppState) =>
  state.entityStores.questionnaireQuestionResponse.entities;

export const selectQuestionnaireQuestionResponseById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireQuestionResponse.entities.get(id);

export const selectQuestionnaireQuestionResponseMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireQuestionResponse.meta.get(id);

export const selectQuestionnaireQuestionResponseAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionnaireQuestionResponseById(id),
    selectQuestionnaireQuestionResponseMetadataById(id),
    (questionnaireQuestionResponse, meta) => ({ questionnaireQuestionResponse, meta }),
  );

export function selectQuestionnaireQuestionResponseEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionnaireQuestionResponseEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionnaireQuestionResponseRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByQuestionnaireResponseId(
  questionnaireResponseId: string,
) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities.filter((e) => e.questionnaireResponseId === questionnaireResponseId).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByQuestionnaireSectionQuestionId(
  questionnaireSectionQuestionId: string,
) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities
      .filter((e) => e.questionnaireSectionQuestionId === questionnaireSectionQuestionId)
      .valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByMatchType(matchType: RuleMatchType) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities.filter((e) => e.matchType === matchType).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByResponseValue(responseValue: string) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities.filter((e) => e.responseValue === responseValue).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByExplanation(explanation: string) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities.filter((e) => e.explanation === explanation).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByReviewRequired(
  reviewRequired: boolean,
) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities.filter((e) => e.reviewRequired === reviewRequired).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllQuestionnaireQuestionResponseEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
